import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Check } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout, { Eyebrow, PrimaryBtn, H2 } from "@/components/public/PublicLayout";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { useCart, imgUrl } from "@/context/CartContext";
import { DEFAULT_PAGES, useSiteContent } from "@/lib/siteContent";

function priceOf(p) {
  const sale = p.sale_price && p.sale_price > 0;
  return { now: sale ? p.sale_price : p.price, was: sale ? p.price : null };
}

function ProductCard({ p }) {
  const { add } = useCart();
  const [added, setAdded] = useState(false);
  const { now, was } = priceOf(p);
  const soldOut = (p.track_inventory && p.stock_qty <= 0) || p.in_stock === false;
  const onAdd = () => { add(p, 1); setAdded(true); setTimeout(() => setAdded(false), 1400); };
  return (
    <div className="rounded-2xl bg-white overflow-hidden flex flex-col" style={{ border: `1px solid ${C.line}` }} data-testid={`product-card-${p.id}`}>
      <Link to={`/shop/product/${p.slug}`} className="block overflow-hidden bg-slate-100" style={{ aspectRatio: "1/1" }}>
        {p.images && p.images[0]
          ? <img src={imgUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition duration-500" loading="lazy" />
          : <div className="w-full h-full flex items-center justify-center text-slate-300"><ShoppingBag className="h-10 w-10" /></div>}
      </Link>
      <div className="p-5 flex flex-col flex-1">
        {p.category ? <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.accent }}>{p.category}</div> : null}
        <Link to={`/shop/product/${p.slug}`} className="text-base font-semibold text-slate-900 leading-snug hover:underline" style={SERIF}>{p.name}</Link>
        <p className="mt-1.5 text-sm text-slate-600 flex-1 line-clamp-2">{p.short_description}</p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-lg font-semibold text-slate-900">${Number(now).toFixed(2)}</span>
          {was ? <span className="text-sm text-slate-400 line-through">${Number(was).toFixed(2)}</span> : null}
        </div>
        <div className="mt-4">
          {soldOut
            ? <button disabled className="w-full rounded-full px-5 py-2.5 text-sm font-semibold bg-slate-100 text-slate-400" data-testid={`sold-out-${p.id}`}>Sold Out</button>
            : <button onClick={onAdd} className="w-full rounded-full px-5 py-2.5 text-sm font-semibold text-white transition inline-flex items-center justify-center gap-2"
                style={{ background: added ? C.green : C.primary }} data-testid={`add-to-cart-${p.id}`}>
                {added ? <><Check className="h-4 w-4" /> Added</> : <>Add to Cart</>}
              </button>}
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const hdr = useSiteContent("pages", DEFAULT_PAGES).shop || DEFAULT_PAGES.shop;
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shop/products");
        setProducts(data.products || []);
        setCats(data.categories || []);
      } catch { setProducts([]); }
    })();
  }, []);

  const shown = products === null ? null : (active === "all" ? products : products.filter((p) => p.category === active));

  return (
    <PublicLayout>
      <section style={{ background: C.plum }} className="text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <Eyebrow dark>{hdr.eyebrow}</Eyebrow>
          <h1 className="text-4xl sm:text-5xl leading-[1.05] max-w-3xl" style={SERIF} data-testid="shop-h1">{hdr.heading}</h1>
          <p className="mt-5 text-base sm:text-lg max-w-2xl" style={{ color: "#D9CCEA" }}>{hdr.subcopy}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {cats.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="shop-categories">
            {["all", ...cats].map((c) => (
              <button key={c} onClick={() => setActive(c)}
                className="text-sm rounded-full px-4 py-1.5 border transition capitalize"
                style={active === c ? { background: C.primary, color: "#fff", borderColor: C.primary } : { background: "#fff", color: C.ink, borderColor: C.line }}
                data-testid={`shop-cat-${c}`}>{c === "all" ? "All Products" : c}</button>
            ))}
          </div>
        )}

        {shown === null ? (
          <p className="text-sm text-slate-500">Loading products…</p>
        ) : shown.length === 0 ? (
          <div className="rounded-2xl p-10 text-center" style={{ border: `1px dashed ${C.line}`, background: C.lightBg }} data-testid="shop-empty">
            <p className="text-slate-700" style={SERIF}>Our product shelf is being prepared.</p>
            <p className="mt-2 text-sm text-slate-600">New KISSED products will appear here soon.</p>
            <div className="mt-6 flex justify-center"><PrimaryBtn to={ROUTES.navigator}>Take the Free Assessment <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="shop-grid">
            {shown.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
